import React, { useState, ChangeEvent } from 'react'
import { SelectChangeEvent } from '@mui/material/Select'
import { useNavigate } from 'react-router-dom'
import UseAlert from '../../components/common/useAlerte'
import { addRisque } from '../../redux/risques/risqueActions'
import SelectPlan from '../../components/common/SelectPlan'
import { PlanSummaryCard } from '../../components/quickCreate/ParentSummaryCard'
import QuickCreate, { QuickCreated, useQuickCreate } from '../../components/quickCreate/QuickCreate'
import { QuickEntity } from '../../components/quickCreate/quickCreateErrors'
import { deadlineOutsidePlan, usePlanInfo } from '../../components/quickCreate/parentInfo'
import { formatTableDate } from '../../utils/formatDate'
import { useSelector } from 'react-redux'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useTranslation } from 'react-i18next'
import { DatePicker } from 'rsuite'
import SelectPriorityRisks from '../../components/common/SelectPriorityRisks'
import SelectCriticality from '../../components/common/IncidentCriticality'
import { createSanitizedObjectHandler } from '../../utils/inputHandlers'
import {
  FormAside,
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormPageHead,
  FormSection,
  FormShell,
  RequiredFieldsProvider,
} from '../../components/common/form'

const RisqueCreate = () => {
  const [PlanId, setPlanId] = useState<string>('')

  const status = useSelector(
    (state: RootState) => state.risque.addedRisque?.status
  )
  const message = useSelector(
    (state: RootState) => state.risque.addedRisque?.message
  )
  console.log(status, message)
  const [priority, setPriority] = useState<number>(3)
  const [Criticality, setCriticality] = useState<number>(1)

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    status: 0,
  })
  const { t, i18n } = useTranslation()
  const planInfo = usePlanInfo(PlanId || undefined)
  const handlePlanChange = (event: SelectChangeEvent<string>) => {
    const newPlan = String(event.target.value)

    setPlanId(newPlan)

    setRisque((prevRisque) => ({
      ...prevRisque,
      PlanId: newPlan.toString(),
    }))

    console.log(PlanId)
  }

  const [risque, setRisque] = useState({
    RisqueName: '',
    Description: '',
    PlanId: '',
    Criticality: 1,
    Priority: 3,
    Deadline: new Date(),
  })

  const dispatch: AppDispatch = useAppDispatch()
  const qc = useQuickCreate()
  const handleQuickCreated = (kind: QuickEntity, created: QuickCreated) => {
    qc.close()
    qc.bump(kind)
    if (kind === 'plan' && created._id) {
      const id = created._id as string
      setPlanId(id)
      setRisque((prev) => ({ ...prev, PlanId: id }))
    }
  }

  const navigate = useNavigate()
  const handleInputChange = createSanitizedObjectHandler(setRisque, {
    RisqueName: 'text',
    Description: 'text',
  })

  const handleDateChange = (value: any) => {
    setRisque((prevRisque) => ({
      ...prevRisque,
      Deadline: value,
    }))
  }

  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    })
    if (alert.status === 200) {
      navigate('/risques')
    }
  }

  type SingleState = string | number | string[]
  const createHandler =
    <T extends SingleState>(
      setState: React.Dispatch<React.SetStateAction<T>>,
      property: string
    ) =>
    (event: ChangeEvent<{ value: T }>) => {
      const { value } = event.target
      setState(value)
      setRisque((prevRisk) => ({
        ...prevRisk,
        [property]: value,
      }))
    }

  const handlePriorityChange = createHandler(setPriority, 'Priority') as (
    event: React.ChangeEvent<{ value: number }> | SelectChangeEvent<number>
  ) => void

  const handleCriticalityChange = createHandler(
    setCriticality,
    'Criticality'
  ) as (
    event: ChangeEvent<{ value: number }> | SelectChangeEvent<number>
  ) => void

  const submitLabel = t('buttons.add')

  const handleAddRisque = async () => {
    const plan = planInfo.data
    if (
      plan &&
      deadlineOutsidePlan(risque.Deadline, plan.DateDebut, plan.DateFin)
    ) {
      setAlert({
        open: true,
        message: t('parentLimits.deadlineOutside', {
          start: formatTableDate(plan.DateDebut, i18n.language),
          end: formatTableDate(plan.DateFin, i18n.language),
        }),
        status: 400,
      })
      return
    }
    if (risque.RisqueName && PlanId) {
      const newrisque = {
        RisqueName: risque.RisqueName,
        Description: risque.Description,
        PlanId: risque.PlanId,
        Criticality: risque.Criticality,
        Priority: risque.Priority,
        Deadline: risque.Deadline,
      }
      await dispatch(addRisque(newrisque))

      setAlert({
        open: true,
        message: message || '',
        status: status || 0,
      })
      console.log(risque)
      setRisque({
        RisqueName: '',
        Description: '',
        PlanId: '',
        Criticality: 0,
        Priority: 0,
        Deadline: new Date(),
      })
    } else {
      setAlert({
        open: true,
        message: 'Please fill out all fields.',
        status: 0,
      })
    }
  }

  return (
    <div className="page-content">
      <FormPageHead
        eyebrow={t('entityForms.risque.createEyebrow', {
          defaultValue: 'Management · Risks',
        })}
        title={t('buttons.addRisk', { defaultValue: 'New risk' })}
        subtitle={t('entityForms.risque.createSubtitle', {
          defaultValue:
            'Register a risk linked to a treatment plan with priority and deadline.',
        })}
        backLabel={t('entityForms.risque.backToList', {
          defaultValue: 'Back to risks',
        })}
        onBack={() => navigate('/risques')}
      />

      <UseAlert
        open={alert.open}
        message={alert.message}
        status={alert.status}
        onClose={handleCloseAlert}
      />

      <RequiredFieldsProvider>
      <FormLayout
        main={
          <FormShell>
            <FormSection
              title={t('entityForms.risque.sectionTitle', {
                defaultValue: 'Risk details',
              })}
            >
              <FormGrid cols={2}>
                <FormField label={t('Risk name')} required>
                  <input
                    className="incyia-form-inp"
                    name="RisqueName"
                    value={risque.RisqueName}
                    onChange={handleInputChange as any}
                    required
                  />
                </FormField>
                <FormField label={t('Treatment plan')} required>
                  <SelectPlan
                    value={risque.PlanId}
                    onChange={handlePlanChange}
                    onCreateNew={qc.open('plan')}
                    reloadKey={qc.keyOf('plan')}
                  />
                </FormField>
                <FormField label={t('Description')} required span>
                  <textarea
                    className="incyia-form-inp"
                    name="Description"
                    value={risque.Description}
                    onChange={handleInputChange as any}
                    rows={4}
                    required
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection
              title={t('entityForms.risque.assessmentTitle', {
                defaultValue: 'Assessment',
              })}
            >
              <FormGrid cols={2}>
                <FormField label={t('Priority')}>
                  <SelectPriorityRisks
                    value={risque.Priority}
                    onChange={handlePriorityChange}
                  />
                </FormField>
                <FormField label={t('Criticality')}>
                  <SelectCriticality
                    value={risque.Criticality}
                    onChange={handleCriticalityChange}
                  />
                </FormField>
                <FormField label={t('Deadline')} span>
                  {/* TODO(backend): a risk does not return its linked tasks, so the deadline cannot be checked against those task dates. */}
                  <DatePicker
                    value={risque.Deadline}
                    onChange={handleDateChange}
                    style={{ width: '100%' }}
                    shouldDisableDate={(date) =>
                      deadlineOutsidePlan(
                        date,
                        planInfo.data?.DateDebut,
                        planInfo.data?.DateFin
                      )
                    }
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormFoot
              note={t('actionCreate.aside.requiredMark', {
                defaultValue: '* Required',
              })}
              cancelLabel={t('buttons.cancel', { defaultValue: 'Cancel' })}
              submitLabel={submitLabel}
              onCancel={() => navigate('/risques')}
              onSubmit={handleAddRisque}
            />
          </FormShell>
        }
        aside={
          <FormAside
            title={t('actionCreate.aside.inThisForm', {
              defaultValue: 'In this form',
            })}
            tipTitle={t('actionCreate.aside.goodToKnow', {
              defaultValue: 'Good to know',
            })}
            tip={t('entityForms.risque.tip', {
              defaultValue:
                'Risks are tracked against their treatment plan and feed into compliance dashboards.',
            })}
            context={
              risque.PlanId ? (
                <>
                  <PlanSummaryCard plain plan={planInfo.data} status={planInfo.status} />
                  <p className="incyia-aside-note incyia-aside-divider">
                    {t('parentLimits.stayInside')}
                  </p>
                </>
              ) : undefined
            }
            steps={[
              {
                title: t('entityForms.risque.sectionTitle', {
                  defaultValue: 'Risk details',
                }),
                fieldCount: 3,
              },
              {
                title: t('entityForms.risque.assessmentTitle', {
                  defaultValue: 'Assessment',
                }),
                fieldCount: 3,
              },
            ]}
            requiredCount={3}
            submitLabel={submitLabel}
            fieldsLabel={(n) =>
              t(
                n === 1
                  ? 'actionCreate.aside.fields_one'
                  : 'actionCreate.aside.fields_other',
                { count: n, defaultValue: `${n} fields` }
              )
            }
            requiredNote={(count, submit) =>
              t('actionCreate.aside.requiredNote', {
                count,
                submit,
                defaultValue: `${count} required before you can ${submit}.`,
              })
            }
          />
        }
      />
      </RequiredFieldsProvider>
      <QuickCreate request={qc.request} onClose={qc.close} onCreated={handleQuickCreated} />
    </div>
  )
}

export default RisqueCreate
